import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Signal,
  computed,
  effect,
  inject,
} from '@angular/core';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdPanelSectionScroller } from '../bodygraph/hd-panel-section-scroller';
import { HdFearCardComponent } from './hd-fear-card.component';
import { HdFearSectionComponent } from './hd-fear-section.component';
import {
  HdCenterFearsPanelData,
  HdFearCenterDefinition,
  getHdFearCenter,
} from './hd-fears.metadata';

interface CenterFearsVm {
  readonly definition: HdFearCenterDefinition;
  readonly title: string;
  readonly paragraphs: readonly string[];
  readonly jumpHint: string;
}

/**
 * Per-center angsten-paneel (Bewustzijn & angsten): bovenaan de rapport-tekst
 * over de angsten van dit bewustzijnscentrum, daaronder de cheat-sheet-blokjes
 * naast het driehoek-diagram (klikken springt naar de detailsectie), en tot
 * slot per poort een volledige {@link HdFearSectionComponent} met de
 * uitgebreide rapport-info. Geopend via `HdFearsPanelService`; een
 * `focusGate` in de paneel-data laat het paneel na render automatisch naar de
 * betreffende poort scrollen (ook bij hergebruik van een al open paneel).
 */
@Component({
  selector: 'hd-center-fears-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdFearCardComponent, HdFearSectionComponent],
  templateUrl: './hd-center-fears-panel.component.html',
  styleUrl: './hd-center-fears-panel.component.scss',
})
export class HdCenterFearsPanelComponent implements AfterViewChecked {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdCenterFearsPanelData>
    | null;

  /** Auto-scroll naar de focus-poort, gedeeld met het voices-paneel. */
  private readonly scroller = new HdPanelSectionScroller(this.host.nativeElement, this.cdr);

  protected readonly vm = computed<CenterFearsVm>(() => {
    const definition = getHdFearCenter(this.panelData?.().center ?? 'spleen');
    return {
      definition,
      title: this.text(`centers.${definition.key}.title`),
      paragraphs: this.list(`centers.${definition.key}.paragraphs`),
      jumpHint: this.text('labels.jumpHint'),
    };
  });

  constructor() {
    // Beide content-talen eager laden: taalwissel + NL-fallback zonder flikker.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');

    // Elke (her)aanlevering van paneel-data met een focus-poort wapent de
    // auto-scroll opnieuw; het scrollen zelf gebeurt in ngAfterViewChecked
    // zodra de sectie gerenderd is en de rapport-tekst geladen is.
    effect(() => {
      const focusGate = this.panelData?.().focusGate ?? null;
      if (focusGate != null) {
        this.scroller.arm(
          `[data-fear-gate="${focusGate}"]`,
          () => this.list(`gates.${focusGate}.paragraphs`).length > 0,
        );
      }
    });

    this.destroyRef.onDestroy(() => this.scroller.dispose());
  }

  ngAfterViewChecked(): void {
    this.scroller.onViewChecked();
  }

  protected scrollToGate(gate: number): void {
    this.scroller.jump(`[data-fear-gate="${gate}"]`);
  }

  /** Late afbeelding-load kan de layout verschuiven → extra view-check. */
  protected onTriangleLoad(): void {
    this.scroller.notifyLayoutShift();
  }

  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('fears', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('fears', key, 'nl');
  }

  private list(key: string): readonly string[] {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetListIn('fears', key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetListIn('fears', key, 'nl');
  }
}
